<script setup lang="ts">
    import { BuildingOfficeIcon } from '@heroicons/vue/24/outline';
    import { useQuery } from '@tanstack/vue-query';
    import { refDebounced } from '@vueuse/core';
    import { toast } from 'vue-sonner';
    import { apiClient, type Entity } from '@/core/api';
    import { useEntitiesStore } from '@/core/stores';
    import { Stack } from '@/shared/ui/atoms/Layout';
    import { Text } from '@/shared/ui/atoms/Typography';
    import { CommandDialog, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/ui/organisms/Command';

    type KvkCompany = { name: string; number: string };

    const emit = defineEmits<{
        /** The company was added. It fires after the dialog closes */
        created: [entity: Entity];
    }>();

    defineSlots<{
        /** What opens it: any component that calls `open`. Bind `triggerAttrs` on it for `aria-expanded`, `aria-controls` and the id focus returns to */
        default?(props: { open: () => void; triggerAttrs: Record<string, unknown> }): unknown;
    }>();

    const messages = useTranslations();
    const entitiesStore = useEntitiesStore();

    const search = ref('');
    const debouncedSearch = refDebounced(
        computed(() => search.value.trim()),
        300,
    );

    const { data: companies, isFetching } = useQuery({
        queryKey: ['lookup', debouncedSearch],
        queryFn: () =>
            apiClient.utils.kvkapi
                .$get([
                    { key: 'search', value: debouncedSearch.value },
                    { key: 'type', value: 'hoofdvestiging' },
                ])
                .then((response) => response.resultaten.map(({ naam, kvkNummer }): KvkCompany => ({ name: naam, number: kvkNummer }))),
        enabled: () => !!debouncedSearch.value,
        retry: false,
    });

    /** Results of an older search stay hidden, so an emptied search shows the hint again */
    const results = computed(() => (search.value.trim() ? (companies.value ?? []) : []));
    const searching = computed(() => !!search.value.trim() && (isFetching.value || search.value.trim() !== debouncedSearch.value));

    /** The KvK number of the company being added. Every item is disabled meanwhile */
    const adding = ref<string>();

    /* The dialog closes before `created`, so a listener that closes the dialog under it through the dialogs store closes that one, not this one. */
    const add = async (company: KvkCompany, close: () => void) => {
        if (adding.value) return;
        adding.value = company.number;
        try {
            const entity = await entitiesStore.createEntity(company);
            toast.success(messages.COMPANY_ADDED({ name: company.name }));
            close();
            emit('created', entity);
        } catch (_) {
            toast.error(messages.ADD_ERROR);
        } finally {
            adding.value = undefined;
        }
    };
</script>

<template>
    <CommandDialog
        v-model:search="search"
        :shortcut="null"
        :should-filter="false"
        :close-on-select="false"
        :aria-label="messages.TITLE"
        @update:open="(open) => open && (search = '')"
    >
        <template #trigger="{ open, triggerAttrs }">
            <slot
                :open="open"
                :trigger-attrs="triggerAttrs"
            />
        </template>

        <template #default="{ close }">
            <CommandInput
                :placeholder="messages.PLACEHOLDER"
                :loading="searching || !!adding"
            />
            <CommandList :aria-label="messages.RESULTS">
                <CommandEmpty>
                    <template v-if="!search.trim()">{{ messages.HINT }}</template>
                    <template v-else-if="searching">{{ messages.SEARCHING }}</template>
                    <template v-else>{{ messages.NO_RESULTS }}</template>
                </CommandEmpty>
                <CommandItem
                    v-for="company in results"
                    :key="company.number"
                    :value="company.number"
                    :disabled="!!adding"
                    @select="add(company, close)"
                >
                    <BuildingOfficeIcon aria-hidden="true" />
                    <Stack gap="0">
                        <Text weight="medium">{{ company.name }}</Text>
                        <Text
                            size="xs"
                            color="tertiary"
                        >
                            {{ messages.KVK_NUMBER({ number: company.number }) }}
                        </Text>
                    </Stack>
                </CommandItem>
            </CommandList>
        </template>
    </CommandDialog>
</template>

<translations lang="yaml">
en:
    TITLE: Add company
    PLACEHOLDER: Search by name or KVK number
    RESULTS: Companies
    HINT: Type a company name or KVK number
    SEARCHING: Searching…
    NO_RESULTS: No companies found
    KVK_NUMBER: 'KVK {number}'
    COMPANY_ADDED: '{name} added'
    ADD_ERROR: The company could not be added
nl:
    TITLE: Bedrijf toevoegen
    PLACEHOLDER: Zoek op naam of KVK-nummer
    RESULTS: Bedrijven
    HINT: Typ een bedrijfsnaam of KVK-nummer
    SEARCHING: Zoeken…
    NO_RESULTS: Geen bedrijven gevonden
    KVK_NUMBER: 'KVK {number}'
    COMPANY_ADDED: '{name} toegevoegd'
    ADD_ERROR: Het bedrijf kon niet worden toegevoegd
</translations>
